/* iter537 — Restaurant dish photo/PDF AI import modal.
 * User uploads a photo of a dish OR a PDF of a menu, types the restaurant
 * name, and GPT-4o vision auto-extracts dish_name + macros + indices.
 */
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Upload, X, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RestaurantImportModal({ isOpen, onClose, onImported, isDark = false }) {
  const [restaurantName, setRestaurantName] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imported, setImported] = useState(null);
  const fileRef = useRef(null);

  if (!isOpen) return null;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (f.type.startsWith("image/")) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview(null);  // PDF — no preview
    }
  };

  const submit = async () => {
    if (!restaurantName.trim()) { toast.error("Въведи името на ресторанта"); return; }
    if (!file) { toast.error("Избери снимка или PDF"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("restaurant_name", restaurantName.trim());
      fd.append("file", file);
      // iter538 — Do NOT set Content-Type manually on FormData uploads:
      // axios needs to inject the multipart boundary string automatically.
      // Hard-coding the header strips that boundary and the server rejects
      // the body. Let axios infer it from the FormData instance.
      const { data } = await axios.post(
        `${API}/restaurants/dish/import-from-image`,
        fd
      );
      setImported(data);
      toast.success(`✅ Внесени са ${data.count} ястия`);
      if (onImported) onImported(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Внасянето не мина");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setRestaurantName(""); setFile(null); setPreview(null); setImported(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className={`w-full max-w-md rounded-2xl p-5 ${
        isDark ? "bg-[hsl(222,47%,11%)] border border-[hsl(223,47%,20%)]" : "bg-white shadow-xl"
      }`} data-testid="restaurant-import-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-base font-bold flex items-center gap-2 ${isDark ? "text-white" : "text-slate-900"}`}>
            <Camera className="w-4 h-4 text-emerald-500" />
            Внасяне на ястия от снимка или PDF
          </h2>
          <button onClick={onClose} data-testid="restaurant-import-close" className="p-1">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {!imported ? (
          <>
            <div className="space-y-3">
              <div>
                <label className={`text-[10px] font-semibold uppercase block mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Име на ресторант *
                </label>
                <input
                  type="text"
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.target.value)}
                  placeholder="напр. Hadjidraganov's Cellars"
                  data-testid="restaurant-import-name"
                  className={`w-full h-10 px-3 rounded-lg text-sm outline-none border ${
                    isDark ? "bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white" : "border-slate-300"
                  }`}
                />
              </div>

              <div>
                <label className={`text-[10px] font-semibold uppercase block mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Снимка на ястие или PDF на меню *
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={onFile}
                  data-testid="restaurant-import-file"
                  className="hidden"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className={`w-full h-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition ${
                    isDark ? "border-[hsl(223,47%,25%)] hover:border-emerald-500 text-gray-300"
                          : "border-slate-300 hover:border-emerald-500 text-slate-600"
                  }`}
                >
                  {preview ? (
                    <img src={preview} alt="preview" className="h-20 w-auto rounded" />
                  ) : file ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      <span className="text-xs">{file.name}</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span className="text-xs">Click или drag&drop</span>
                    </>
                  )}
                </button>
              </div>

              <p className={`text-[10px] italic ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                💡 AI ще извлече ястията + ще изчисли макроси + DIL/NIS/PRAL/GI/GL/Inflammation/COL3/FODMAP автоматично.
              </p>

              <button
                onClick={submit}
                disabled={loading}
                data-testid="restaurant-import-submit"
                className="w-full h-10 rounded-lg text-sm font-semibold text-white
                  bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600
                  disabled:opacity-50 transition flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Анализирам с AI...</> : <>🤖 Анализирайте и внесете</>}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className={`rounded-lg p-3 ${isDark ? "bg-emerald-900/20" : "bg-emerald-50"}`}>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className={`font-semibold text-sm ${isDark ? "text-white" : "text-slate-900"}`}>
                  Внесени са {imported.count} ястия в {imported.restaurant_name}
                </span>
              </div>
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {(imported.dishes_imported || []).map((d, i) => (
                  <div key={i} className={`p-2 rounded text-xs ${isDark ? "bg-[hsl(223,47%,15%)] text-gray-300" : "bg-white text-slate-700"}`}>
                    <div className="font-semibold">{d.dish_name}</div>
                    <div className="flex gap-2 mt-1 flex-wrap text-[10px]">
                      <span className="text-rose-500">DIL {d.dil_g}g</span>
                      {d.nis_percent != null && <span className="text-rose-500">NIS {d.nis_percent}%</span>}
                      {d.gi != null && <span className="text-amber-500">GI {d.gi}</span>}
                      {d.gl != null && <span className="text-orange-500">GL {d.gl}</span>}
                      {d.pral_meq != null && <span className="text-purple-500">PRAL {d.pral_meq}</span>}
                      {d.inflammation_score != null && <span className="text-red-500">🔥{d.inflammation_score}/10</span>}
                      {d.col3_synthesis_score != null && <span className="text-emerald-500">COL3 {d.col3_synthesis_score}/10</span>}
                      {d.fodmap_tier && <span className="text-yellow-600">FODMAP:{d.fodmap_tier}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={reset}
                className={`flex-1 h-10 rounded-lg text-sm font-medium ${
                  isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                }`}
              >
                Внесете още
              </button>
              <button
                onClick={onClose}
                className="flex-1 h-10 rounded-lg text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600"
              >
                Готово
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
